import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Quote from "@/pages/Quote";
import About from "@/pages/About";
import Track from "@/pages/Track";
import Contact from "@/pages/Contact";
import Fulfillment from "@/pages/Fulfillment";
import Shop from "@/pages/Shop";
import FreightQuote from "@/pages/FreightQuote";
import FreightRoutePage from "@/pages/FreightRoutePage";

import { AdminAuthProvider } from "@/admin/AdminAuth";
import { AdminGuard, AdminLayout } from "@/admin/AdminLayout";
import AdminLogin from "@/admin/AdminLogin";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminLeads from "@/admin/AdminLeads";
import AdminFreightQuotes from "@/admin/AdminFreightQuotes";
import AdminSources from "@/admin/AdminSources";
import AdminMerchants from "@/admin/AdminMerchants";
import AdminImport from "@/admin/AdminImport";
import AdminHubs from "@/admin/AdminHubs";
import AdminOutbox from "@/admin/AdminOutbox";

import { PortalAuthProvider, PortalGuard, portalApi, usePortalAuth } from "@/portal/PortalAuth";
import { PortalLogin, PortalRegister, PortalLayout, PortalDashboard, PortalSubscribe, PortalSubscribeSuccess, PortalNewShipment, PortalBulk, PortalAddresses, PortalTeam, PortalJoin, PortalTemplates } from "@/portal/PortalPages";
import { PortalFulfillment } from "@/portal/ShipperPages";
import { OpsAuthProvider, OpsGuard } from "@/ops/OpsAuth";
import { OpsLogin, OpsLayout, OpsDashboard, OpsReceiving, OpsBins, OpsOrders, OpsOrderDetail } from "@/ops/OpsPages";
import FloatingAssistant from "@/components/FloatingAssistant";

import "@/App.css";

const PublicSite = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/services" element={<Services />} />
      <Route path="/fulfillment" element={<Fulfillment />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/freight/quote" element={<FreightQuote />} />
      <Route path="/freight/:slug" element={<FreightRoutePage />} />
      <Route path="/quote" element={<Quote />} />
      <Route path="/about" element={<About />} />
      <Route path="/track" element={<Track />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="*" element={<Home />} />
    </Routes>
  </Layout>
);

const AuthCallback = () => {
  const nav = useNavigate();
  const { setCustomer } = usePortalAuth();
  const location = useLocation();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/portal/login", { replace: true }); return; }
    const sid = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await portalApi.googleSession(sid);
        setCustomer(r.data.customer);
        // Clean hash so future refreshes don't retry
        window.history.replaceState(null, "", "/portal");
        nav("/portal", { replace: true });
      } catch (e) {
        window.history.replaceState(null, "", "/portal/login");
        nav("/portal/login", { replace: true, state: { error: "Google sign-in failed" } });
      }
    })();
  }, [location.hash, nav, setCustomer]);
  return (
    <div className="min-h-screen grid place-items-center bg-[var(--tt-cream)] text-sm text-[var(--tt-muted)]">
      <div className="text-center space-y-3">
        <div className="mx-auto h-10 w-10 rounded-full border-4 border-[var(--tt-green)] border-t-transparent animate-spin" />
        <div>Signing you in with Google…</div>
      </div>
    </div>
  );
};

const RouterInner = () => {
  const location = useLocation();
  // Detect the OAuth callback fragment BEFORE the Routes render — synchronous, no useEffect
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/*"
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="freight-quotes" element={<AdminFreightQuotes />} />
        <Route path="sources" element={<AdminSources />} />
        <Route path="hubs" element={<AdminHubs />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="outbox" element={<AdminOutbox />} />
        <Route path="import" element={<AdminImport />} />
      </Route>

      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/join" element={<PortalJoin />} />
      <Route path="/portal/*" element={<PortalGuard><PortalLayout /></PortalGuard>}>
        <Route index element={<PortalDashboard />} />
        <Route path="new" element={<PortalNewShipment />} />
        <Route path="addresses" element={<PortalAddresses />} />
        <Route path="bulk" element={<PortalBulk />} />
        <Route path="team" element={<PortalTeam />} />
        <Route path="templates" element={<PortalTemplates />} />
        <Route path="subscribe" element={<PortalSubscribe />} />
        <Route path="subscribe/success" element={<PortalSubscribeSuccess />} />
        <Route path="fulfillment" element={<PortalFulfillment />} />
      </Route>

      <Route path="/ops/login" element={<OpsLogin />} />
      <Route path="/ops/*" element={<OpsGuard><OpsLayout /></OpsGuard>}>
        <Route index element={<OpsDashboard />} />
        <Route path="receiving" element={<OpsReceiving />} />
        <Route path="bins" element={<OpsBins />} />
        <Route path="orders" element={<OpsOrders />} />
        <Route path="orders/:orderId" element={<OpsOrderDetail />} />
      </Route>

      <Route path="/*" element={<PublicSite />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <PortalAuthProvider>
            <OpsAuthProvider>
              <RouterInner />
              <FloatingAssistant />
            </OpsAuthProvider>
          </PortalAuthProvider>
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
